import type { SearchScenarioId } from 'owa-search-store';
import SearchScenarioIdValue from 'owa-search-store/lib/store/schema/SearchScenarioId';
import { action } from 'satcheljs';
import { addDatapointConfig } from 'owa-analytics-actions';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';

/**
 * Action dispatched when suggestions should be set in the store. Consuming client
 * should set up an orchestrator to subscribe to it, which should read appropriate
 * values from the search store, fetch suggestions, and set them in the store.
 */
export interface GET_SUGGESTIONSActionMessage {
    scenarioId: SearchScenarioId;
}

/**
 * Scenarios with a lazy `getSuggestions` orchestrator, which are the only ones
 * positioned to own the `Search_SuggestionsE2E` lifecycle.
 *
 * A scenario that dispatches this action with no subscriber yields an event
 * nobody can end; the focus-gated one-shot timeout then turns it into a false
 * `Timeout` or a datapoint that never ends.
 *
 * A registered scenario belongs here even before it ends the datapoint itself —
 * dropping it would emit nothing at all for that scenario, which is worse.
 *
 * The enum comes from its own module rather than the package root; a value
 * import there would pull the whole store into every search-box bundle.
 */
const SCENARIOS_WITH_SUGGESTIONS_OWNER: readonly SearchScenarioId[] = [
    SearchScenarioIdValue.Mail,
    SearchScenarioIdValue.Calendar,
    SearchScenarioIdValue.People,
];

function hasSuggestionsDatapointOwner(scenarioId: SearchScenarioId): boolean {
    return SCENARIOS_WITH_SUGGESTIONS_OWNER.indexOf(scenarioId) >= 0;
}

export const getSuggestions: (scenarioId: SearchScenarioId) => GET_SUGGESTIONSActionMessage =
    action(
        'GET_SUGGESTIONS',
        (scenarioId: SearchScenarioId): GET_SUGGESTIONSActionMessage =>
            hasSuggestionsDatapointOwner(scenarioId)
                ? addDatapointConfig(
                      'Search_SuggestionsE2E',
                      getModuleContextMailboxInfo(),
                      {
                          unifiedTelemetry: {
                              eventName: 'PerformSearch',
                              data: {},
                          },
                      },
                      {
                          scenarioId,
                      }
                  )
                : { scenarioId }
    );
